import React, { useState, useEffect } from 'react'
import { ChevronUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'

interface BackToTopProps {
  /** Scroll threshold in pixels before button appears (default: 300) */
  threshold?: number
  /** Additional CSS classes to apply */
  className?: string
}

/**
 * BackToTop Button Component
 * 
 * A floating action button that appears when the user scrolls down and 
 * provides smooth scrolling back to the top of the page.
 * 
 * Features:
 * - Appears/disappears based on scroll position
 * - Smooth scroll animation
 * - Responsive design for mobile and desktop
 * - Accessibility support (keyboard navigation, ARIA labels)
 * - Reduced motion support
 * - Framer Motion animations
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <BackToTop />
 * 
 * // Custom threshold
 * <BackToTop threshold={500} />
 * 
 * // With custom styling
 * <BackToTop className="custom-styles" />
 * ```
 */

const BackToTop: React.FC<BackToTopProps> = ({ 
  threshold = 300, 
  className = '' 
}) => {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)
  const [isScrolling, setIsScrolling] = useState(false)

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > threshold) {
        setIsVisible(true)
      } else {
        setIsVisible(false)
      }
    }

    const handleScroll = () => {
      setIsScrolling(true)
      toggleVisibility()
      
      // Clear the scrolling state after a brief delay
      setTimeout(() => setIsScrolling(false), 150)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [threshold])

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      scrollToTop()
    }
  }

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ 
            opacity: 1, 
            scale: isScrolling ? 0.95 : 1, 
            y: 0 
          }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          transition={{ 
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1]
          }}
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          className={`
            back-to-top
            fixed bottom-6 left-6 z-50
            w-12 h-12 
            bg-gradient-to-r from-blue-600 to-green-600 
            hover:from-blue-500 hover:to-green-500
            text-white 
            rounded-full 
            border border-white/20
            flex items-center justify-center
            focus:outline-none focus:ring-4 focus:ring-blue-500/30
            group
            ${className}
          `}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label={t('common.backToTop')}
          title={t('common.backToTop')}
        >
          <motion.div
            animate={{ y: isScrolling ? -1 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronUp 
              className="h-6 w-6 group-hover:animate-bounce" 
              strokeWidth={2.5}
            />
          </motion.div>
          
          {/* Ripple effect on hover */}
          <motion.div
            className="absolute inset-0 rounded-full bg-white/10"
            initial={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3 }}
          />
        </motion.button>
      )}
    </AnimatePresence>
  )
}

export default BackToTop
